import { QueryClient } from '@tanstack/react-query';

/**
 * One client, in its own module so non-component code (the admin actions) can
 * invalidate queries without being handed it through props or context.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Loaders already fetch on navigation; refetching again the moment the
      // tab regains focus just makes screens flicker for no new information.
      refetchOnWindowFocus: false,
      // No retry. R-144.
      //
      // The retryer is what pauses. After one failure it waits for a condition
      // that never arrives in a background tab, the query sits at
      // `fetchStatus: 'paused'` for ever, the route loader awaits it, and the
      // page is blank. `networkMode: 'always'` alone did not release it.
      //
      // Retrying was worth little here anyway: these calls go to the same host
      // that served the page, and a 500 from our own API is not flaky, it is
      // wrong. Failing at once shows the reason instead of hiding it.
      retry: false,
      staleTime: 30_000,
      // The blank page. R-144.
      //
      // TanStack Query PAUSES a query when it believes the browser is offline,
      // and a paused query never settles. The route loader awaits it, the
      // router sits at `status: 'pending'` with no matches, and React renders
      // null: an empty page, no error, no spinner, silent console. That is the
      // defect STATUS carried as open, and three attempts at error components
      // could not fix it because nothing ever failed.
      //
      // `navigator.onLine` is wrong more often than it is useful. It is false
      // in some automated browsers, false on a captive portal, and true on a
      // wifi network with no route to anywhere. This application talks to the
      // host it was served from: if that request fails, the honest answer is a
      // failure, not a wait with no end.
      networkMode: 'always',
    },
    // The same for writes. A paused mutation is a save that never happens and
    // never says so.
    mutations: { networkMode: 'always' },
  },
});

/**
 * Call after any sign-in or sign-out.
 *
 * Signing in or out changes *who the cached data belongs to*, so every entry is
 * suspect afterwards — not just `me`, but orders, quotes and the account
 * screen, which would otherwise still be holding the previous person's rows.
 *
 * It also fixes a specific trap. The admin gate reads `me` through
 * `ensureQueryData`, which returns the cached value while it is fresh. Without
 * this, signing in left a 30-second-fresh `{ isAdmin: false }` in the cache, so
 * navigating to /admin immediately after a *successful* sign-in read the stale
 * "signed out" answer and redirected straight back to the sign-in page.
 *
 * `router.invalidate()` does not cover this: it re-runs route loaders, but the
 * loaders read through this cache, so the stale answer survives it.
 */
export function resetIdentity(): void {
  queryClient.clear();
}
